import React, { useState } from 'react';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  IconButton
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { httpRequest } from 'src/services/httpRequest';

interface ForgotPasswordFormData {
  email: string;
}

function ForgotPasswordPage() {
  const { state: locationState, township, uuid } = useParams();

  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState<ForgotPasswordFormData>({
    email: ''
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
    if (success) setSuccess('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await httpRequest('POST', `/auth/reset-password-otp/${locationState}/${township}/${uuid}`, {
        EmailID: formData.email
      });

      if (response?.status === 'success') {
        setSuccess('OTP has been sent to your email address');
        // Navigate to OTP verification page with email
        setTimeout(() => {
          navigate(
            `/${locationState}/${township}/${uuid}/forgot-password/verify-otp`,
            {
              state: { email: formData.email }
            }
          );
        }, 1500);
      } else {
        setError(response?.message || 'Failed to send OTP. Please try again.');
      }
    } catch (err) {
      setError(err.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate(`/${locationState}/${township}/${uuid}/login`);
  };

  return (
    <Box
      sx={{
        minHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#f5f5f5'
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center'
        }}
      >
        <IconButton
          onClick={handleBackToLogin}
          sx={{
            color: 'primary.main',
            '&:hover': {
              backgroundColor: 'primary.light'
            }
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" sx={{ ml: 1, color: 'primary.main' }}>
          Back to Login
        </Typography>
      </Box>

      {/* Main Content */}
      <Box
        sx={{
          mt: 8
        }}
      >
        <Container maxWidth="sm">
          <Card
            sx={{
              borderRadius: '16px',
              boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
              overflow: 'visible'
            }}
          >
            <CardContent sx={{ p: 0 }}>
              <form onSubmit={handleSubmit}>
                <Box sx={{ p: 4, pb: 3 }}>
                  <Typography
                    variant="h4"
                    sx={{
                      textAlign: 'center',
                      fontSize: '1.5rem',
                      fontWeight: 'bold',
                      mb: 2
                    }}
                  >
                    Forgot Password
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    align="center"
                    sx={{ mb: 3 }}
                  >
                    Enter your email address and we'll send you an OTP to reset
                    your password
                  </Typography>

                  {error && (
                    <Alert
                      severity="error"
                      sx={{
                        mb: 3,
                        borderRadius: '8px',
                        '& .MuiAlert-icon': { alignItems: 'center' }
                      }}
                    >
                      {error}
                    </Alert>
                  )}

                  {success && (
                    <Alert
                      severity="success"
                      sx={{
                        mb: 3,
                        borderRadius: '8px',
                        '& .MuiAlert-icon': { alignItems: 'center' }
                      }}
                    >
                      {success}
                    </Alert>
                  )}

                  <TextField
                    autoFocus
                    name="email"
                    label="Email Address"
                    type="email"
                    fullWidth
                    variant="outlined"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    disabled={loading}
                    sx={{
                      mb: 3,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '10px'
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <Box
                          component="span"
                          sx={{
                            color: 'action.active',
                            mr: 1,
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <rect width="20" height="16" x="2" y="4" rx="2" />
                            <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                          </svg>
                        </Box>
                      )
                    }}
                  />
                </Box>

                <Box sx={{ p: 4, pt: 0 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={loading || !formData.email.trim()}
                    fullWidth
                    sx={{
                      borderRadius: '10px',
                      py: 1.5,
                      textTransform: 'none',
                      fontWeight: 'bold',
                      fontSize: '1rem',
                      boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)'
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      'Send OTP'
                    )}
                  </Button>

                  <Button
                    onClick={handleBackToLogin}
                    disabled={loading}
                    variant="text"
                    fullWidth
                    sx={{
                      mt: 2,
                      textTransform: 'none',
                      color: 'text.secondary',
                      '&:hover': {
                        backgroundColor: 'transparent',
                        textDecoration: 'underline'
                      }
                    }}
                  >
                    Back to Login
                  </Button>
                </Box>
              </form>
            </CardContent>
          </Card>
        </Container>
      </Box>
    </Box>
  );
}

export default ForgotPasswordPage;
